import { Stack, Typography, useTheme } from '@mui/material'
import { RadioReceiverIcon, ShoppingBagIcon, InfoIcon } from 'lucide-react'
import { useNavigate } from 'react-router-dom'

import { Button } from '@repo/ui'

import { ROUTES } from 'routes'
import { SHOP_PRODUCT_TYPE } from 'constants/tagShopProductTypes'

/**
 * "You have no desk reader — here is what to do about it."
 *
 * SHARED because it is now the answer in two places: the Create Certificates scan block
 * (`ScanSessionBlock`) and the filter scan-session connect modal (`ScanConnectModal`), whose
 * "Desk reader — Not connected" row was previously a dead end that stated the fact and offered
 * nothing. Extracted on the SECOND occurrence rather than hand-rolled again
 * (working-conventions §36) — the two would otherwise drift on the copy, the glyph and which
 * routes the buttons take, which is exactly what happened to the payment-method preview.
 *
 * NOT the shared `EmptyState` (§28 says reuse first — this is the case where reuse was wrong).
 * `EmptyState` is built for a full-page empty TABLE: a 72px filled circle plate behind the icon,
 * an optional title, then body copy, then buttons. Dropped into a 42px modal row it read as
 * crowded and far heavier than what it says — a page-level graphic announcing one sentence.
 *
 * The treatment here is the in-modal placeholder the app already uses for exactly this weight:
 * `PackageBoxModal`'s "Select a package variant to begin." — a BARE glyph at 32px in
 * `text.disabled` with no plate behind it, one line of `body3` secondary copy, `spacing={2}`,
 * and `py: 6` doing the breathing instead of a big graphic. Same information, a quarter of the
 * visual weight.
 *
 * Owns its own vertical padding so both callers render it bare inside their accordion.
 *
 * DEFAULT CLOSED is a caller-side rule, but it is what this panel is sized for: even at this
 * lighter weight it is setup guidance for hardware the operator may not own, and
 * `ScanSessionBlock` used to force it open whenever no reader was connected. That was
 * deliberately undone; it must not come back.
 */
export const ReaderSetupPanel = ({
  connected,
  onNavigate,
}: {
  connected: boolean
  /**
   * Run just BEFORE either action navigates away — how a caller that lives in a modal
   * closes it.
   *
   * Required because the surface this panel most often sits in, `ScanConnectModal`, is
   * mounted globally under `ScanRoutingProvider` rather than by a page: navigating from
   * inside it changed the route underneath while the modal stayed up, so the operator
   * arrived at the shop with a "Connect a scanner" dialog covering it. A modal owned by a
   * page unmounts with the route and never had the problem, which is why it survived.
   *
   * The callback rather than a `useLocation` watcher in the provider: closing is a
   * consequence of THIS click, and a route-change effect would also fire on navigations
   * the modal has nothing to do with.
   */
  onNavigate?: () => void
}) => {
  const theme = useTheme()
  const navigate = useNavigate()

  const go = (route: string) => {
    onNavigate?.()
    navigate(route)
  }

  return (
    <Stack alignItems="center" spacing={2} sx={{ py: 6 }}>
      <RadioReceiverIcon
        strokeWidth={1}
        size={32}
        color={connected ? theme.palette.brand[500] : theme.palette.text.disabled}
      />
      <Typography
        variant="body3"
        color="text.secondary"
        sx={{ maxWidth: 320, textAlign: 'center', lineHeight: 1.5 }}
      >
        {connected
          ? 'Reader connected and ready to scan.'
          : 'No reader connected. Plug in a Vera Reader, or get one from the shop.'}
      </Typography>
      {/* TWO PEERS, in journey order, and neither of them shouts.
          This used to be a bordered "Shop readers" button with a 12px underlined link
          beneath it — the loudest control on the panel sending an uninformed person to a
          shop, and the explanation of what they'd be buying rendered as the quietest thing
          on screen. Anyone these controls are FOR is in one of two states: they own a
          reader and it is unplugged (in which case the sentence above is what they need,
          not a button), or they do not own one — and for that person the sequence is
          understand, then buy. So: equal weight, left to right in that order.

          Both tertiary (§32's no-border tier) because this is an empty-state helper inside
          a panel, not the page's call to action. A filled or bordered button here outweighs
          the actual content of the screen. §29: 12px between a pair acting as a group.

          "How readers work" rather than "Learn more" — a peer button has to say what is on
          the other side of it, and "what does this thing even do" is the exact question the
          old layout left unanswered. */}
      {/* WRAPS, because this panel now also renders inside a 340px popover (the filter scan-source
          picker), where the two buttons need 345px side by side and "Shop readers" was clipped by
          the paper's edge. Wrapping costs the modal nothing — it is wide enough that the pair
          never breaks — and it degrades to a centred column rather than a cut-off control. */}
      <Stack
        direction="row"
        flexWrap="wrap"
        justifyContent="center"
        useFlexGap
        spacing={3}
        alignItems="center"
        sx={{ pt: 2 }}
      >
        <Button
          variant="tertiary"
          disableElevation
          startIcon={<InfoIcon strokeWidth={1} size={18} />}
          onClick={() => go(ROUTES.READER_SETUP)}
        >
          How readers work
        </Button>
        {!connected && (
          <Button
            variant="tertiary"
            disableElevation
            startIcon={<ShoppingBagIcon strokeWidth={1} size={18} />}
            // Lands on the shop already narrowed to readers, via Shopify's per-product
            // `Type` field (`category_type`) — not a title match. "Shop readers" rather
            // than "Buy a reader" because that is what the click does: it opens a filtered
            // catalog, it does not buy anything.
            onClick={() => go(ROUTES.TAGS_SHOP_BY_TYPE(SHOP_PRODUCT_TYPE.HARDWARE))}
          >
            Shop readers
          </Button>
        )}
      </Stack>
    </Stack>
  )
}
